// @ts-nocheck
/**
 * Company Hierarchy modal — first shows a picker of users assigned to the
 * company, then drills into that user's hierarchy (reuses the same
 * `HierarchyNode` / `HIERARCHY_STYLES` block as the user-hierarchy modal).
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Building2, User, Loader2 } from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { API_USERS_LIST, API_USER_HIERARCHY } from '@/service/url';
import { USERS_ROLE } from '@/utilities/constant';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HierarchyView, HIERARCHY_STYLES } from './user-hierarchy-modal';

function RenderCompanyHierarchyModal({ show, setShow, company }) {
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState('');
  const [tree, setTree] = useState(null);
  const [total, setTotal] = useState(0);

  const companyId = company?.id ?? null;

  const handleOpenChange = (next) => {
    setShow(next);
    if (!next) {
      setUsers([]);
      setUsersError('');
      setSelectedUser(null);
      setTree(null);
      setTreeError('');
      setTotal(0);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!show || !companyId) return undefined;

    setUsersLoading(true);
    setUsersError('');
    setUsers([]);
    setSelectedUser(null);
    setTree(null);
    setTreeError('');

    getApiCall(API_USERS_LIST, {})
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp) ? resp : [];
        const assigned = list.filter(
          (u) =>
            !u?.is_deleted &&
            Number(u?.assigned_company ?? u?.assignedCompany?.id) === Number(companyId),
        );
        setUsers(assigned);
        if (assigned.length === 1) setSelectedUser(assigned[0]);
      })
      .catch((err) => {
        if (cancelled) return;
        setUsersError(typeof err === 'string' ? err : err?.message || 'Failed to load company users');
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, companyId]);

  useEffect(() => {
    let cancelled = false;
    if (!show || !selectedUser?.id) return undefined;

    setTreeLoading(true);
    setTreeError('');
    setTree(null);

    getApiCall(API_USER_HIERARCHY.replace(':id', selectedUser.id))
      .then((data) => {
        if (cancelled) return;
        setTree(data?.tree || null);
        setTotal(data?.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setTreeError(typeof err === 'string' ? err : err?.message || 'Failed to load user hierarchy');
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, selectedUser?.id]);

  const canGoBackToUsers = users.length > 1;

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => String(a?.username || '').localeCompare(String(b?.username || ''))),
    [users],
  );

  const renderUserPicker = () => (
    <>
      <p className="mb-2 text-xs text-muted-foreground">
        {sortedUsers.length} user(s) assigned to this company — pick one to view its hierarchy
      </p>
      <ul className="space-y-1.5">
        {sortedUsers.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => setSelectedUser(u)}
              className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2 text-left transition-colors hover:border-primary"
            >
              <span className="uh-avatar" aria-hidden="true">
                <User size={14} strokeWidth={2.25} />
              </span>
              <span className="uh-username">{u.username || u.name || '—'}</span>
              {u.role ? <span className="uh-role">{USERS_ROLE[u.role] || u.role}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  const renderTree = () => {
    if (treeLoading) {
      return (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading hierarchy…
        </div>
      );
    }
    if (treeError) return <div className="py-6 text-center text-sm text-destructive">{treeError}</div>;
    if (!tree) return <div className="py-6 text-center text-sm text-muted-foreground">No hierarchy found</div>;
    return (
      <>
        <HierarchyView
          tree={tree}
          summary={<p className="m-0 text-xs text-muted-foreground">{total} user(s) in this hierarchy</p>}
        />
      </>
    );
  };

  const renderBody = () => {
    if (usersLoading) {
      return (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading company users…
        </div>
      );
    }
    if (usersError) return <div className="py-6 text-center text-sm text-destructive">{usersError}</div>;
    if (users.length === 0) {
      return <div className="py-6 text-center text-sm text-muted-foreground">No users assigned to this company</div>;
    }
    return selectedUser ? renderTree() : renderUserPicker();
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} />
            Company Hierarchy
            {company?.name ? (
              <span className="text-sm font-normal text-muted-foreground">— {company.name}</span>
            ) : null}
          </DialogTitle>
          {selectedUser ? (
            <DialogDescription>
              Hierarchy of <span className="font-medium text-foreground">
                {selectedUser.username || selectedUser.name || '—'}
              </span>
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <style>{HIERARCHY_STYLES}</style>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {selectedUser && canGoBackToUsers ? (
            <button
              type="button"
              onClick={() => {
                setSelectedUser(null);
                setTree(null);
                setTreeError('');
              }}
              className="mb-2 inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:text-primary/80"
            >
              <ArrowLeft size={13} /> Back to users
            </button>
          ) : null}

          {renderBody()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RenderCompanyHierarchyModal;
